import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft,
  Save,
  X,
  Plus,
  Calendar,
  Clock,
  User,
  Target,
  AlertTriangle,
  FileText,
  Settings,
  Tag,
  Link,
  Paperclip,
  MessageSquare,
  CheckCircle,
  Building,
  Phone,
  Mail,
  MapPin,
  DollarSign,
  Users,
  Briefcase,
  Shield,
  BarChart3,
  Upload,
  Star,
  Award,
  UserCheck,
  FileCheck,
  CreditCard,
  Globe,
  Lock,
  Eye,
  Edit,
  Trash2,
  TrendingUp,
  TrendingDown,
  PieChart,
  Activity,
  Zap,
  Award as AwardIcon,
  ThumbsUp,
  ThumbsDown,
  AlertCircle,
  CheckCircle2,
  XCircle,
  FileSearch,
  Clipboard,
  BookOpen,
  Scale,
  Gavel,
  Badge,
  Certificate,
  Stamp,
  Seal
} from 'lucide-react';
import { useToast, ToastContainer } from '../common/Toast';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import ComplianceService from '../../services/complianceService';
import SubcontractingService from '../../services/subcontractingService';
import ContractService from '../../services/contractService';

const CreateComplianceRecord = ({ editMode = false, viewMode = false, complianceId = null }) => {
  const navigate = useNavigate();
  const { success, error: showError, toasts, removeToast } = useToast();
  
  // Tab management
  const [activeTab, setActiveTab] = useState('basic');
  
  // Loading state
  const [loading, setLoading] = useState(editMode || viewMode);
  const [contracts, setContracts] = useState([]);
  const [loadingContracts, setLoadingContracts] = useState(false);
  // Form state
  const [formData, setFormData] = useState({
    // Basic Information
    contractId: '',
    recordId: '',
    recordType: 'compliance',
    status: 'draft',
    priority: 'medium',
    effectiveDate: '',
    expiryDate: '',
    reviewDate: '',
    
    // Subcontractor Information
    subcontractor: {
      id: '',
      name: '',
      company: '',
      category: '',
      contactPerson: '',
      email: '',
      phone: ''
    },
    
    // Compliance Details
    complianceType: '',
    complianceCategory: '',
    regulatoryFramework: '',
    applicableStandards: [],
    complianceLevel: '',
    riskLevel: '',
    
    // Requirements and Standards
    requirements: [],
    standards: [],
    regulations: [],
    policies: [],
    procedures: [],
    
    // Documentation
    documents: [],
    certificates: [],
    licenses: [],
    permits: [],
    approvals: [],
    
    // Assessment Details
    assessment: {
      overallScore: 0,
      complianceScore: 0,
      riskScore: 0,
      qualityScore: 0,
      safetyScore: 0,
      environmentalScore: 0,
      socialScore: 0,
      governanceScore: 0
    },
    
    // Violations and Issues
    violations: [],
    nonCompliances: [],
    issues: [],
    correctiveActions: [],
    preventiveActions: [],
    
    // Audit Information
    audit: {
      lastAuditDate: '',
      nextAuditDate: '',
      auditor: '',
      auditType: '',
      auditScope: '',
      auditFindings: [],
      auditRecommendations: []
    },
    
    // Certifications and Licenses
    certifications: {
      iso9001: { status: 'not-applicable', expiryDate: '', certificateNumber: '' },
      iso14001: { status: 'not-applicable', expiryDate: '', certificateNumber: '' },
      ohsas18001: { status: 'not-applicable', expiryDate: '', certificateNumber: '' },
      iso45001: { status: 'not-applicable', expiryDate: '', certificateNumber: '' },
      custom: []
    },
    
    // Training and Competency
    training: {
      requiredTraining: [],
      completedTraining: [],
      trainingRecords: [],
      competencyAssessments: [],
      skillGaps: []
    },
    
    // Monitoring and Reporting
    monitoring: {
      frequency: '',
      method: '',
      responsible: '',
      reportingSchedule: '',
      escalationProcedure: ''
    },
    
    // Risk Management
    riskAssessment: {
      identifiedRisks: [],
      riskLevels: [],
      mitigationStrategies: [],
      contingencyPlans: [],
      riskOwners: []
    },
    
    // Performance Metrics
    metrics: {
      complianceRate: 0,
      violationCount: 0,
      auditScore: 0,
      trainingCompletion: 0,
      certificationStatus: 0
    },
    
    // Review and Approval
    review: {
      reviewer: '',
      reviewDate: '',
      approvalStatus: 'pending',
      approvalNotes: '',
      nextReviewDate: ''
    },
    
    // Advanced Settings
    settings: {
      autoReminders: true,
      escalationEnabled: true,
      publicVisibility: false,
      requireApproval: true,
      notificationSettings: {
        email: true,
        sms: false,
        portal: true
      }
    }
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Helper function to get disabled/readOnly props for form inputs
  const getInputProps = () => ({
    disabled: viewMode,
    readOnly: viewMode
  });

  // State for subcontractors dropdown
  const [subcontractors, setSubcontractors] = useState([]);

  // Load subcontractors for dropdown
  useEffect(() => {
    const loadSubcontractors = async () => {
      try {
        const response = await SubcontractingService.getSubcontractings({ limit: 100 });
        if (response && response.success) {
          setSubcontractors(response.data || []);
        }
      } catch (err) {
        console.error('Error loading subcontractors:', err);
      }
    };
    loadSubcontractors();
  }, []);
  
  const loadContracts = async () => {
  setLoadingContracts(true);
  try {
    const response = await ContractService.getContracts();
    if (response.success) {
      // Use the same normalization logic as BillingPayments.js
      const contractsList = (response.data?.contracts || response.data || [])
        .filter(contract => contract._id);
      
      const normalized = contractsList.map(contract => ({
        ...contract,
        displayLabel: `${contract.title || contract.contractId || 'Contract'}${contract.contractId ? ` (${contract.contractId})` : ''}`
      }));
      
      setContracts(normalized);
    }
  } catch (err) {
    console.error('Error loading contracts for compliance:', err);
  } finally {
    setLoadingContracts(false);
  }
};

// Call it on mount
useEffect(() => {
  loadContracts();
}, []);

  // Load compliance data if in edit or view mode
  useEffect(() => {
    const loadComplianceData = async () => {
      if (complianceId && (editMode || viewMode)) {
        setLoading(true);
        try {
          const response = await ComplianceService.getCompliance(complianceId);
          if (response && response.success) {
            // Map backend data to form data
            const compliance = response.data;
            setFormData({
              recordId: compliance.recordId || '',
              recordType: compliance.recordType || 'compliance',
              status: compliance.status || 'draft',
              priority: compliance.priority || 'medium',
              effectiveDate: compliance.effectiveDate ? new Date(compliance.effectiveDate).toISOString().split('T')[0] : '',
              expiryDate: compliance.expiryDate ? new Date(compliance.expiryDate).toISOString().split('T')[0] : '',
              reviewDate: compliance.reviewDate ? new Date(compliance.reviewDate).toISOString().split('T')[0] : '',
              subcontractor: compliance.subcontractor || {
                id: '',
                name: '',
                company: '',
                category: '',
                contactPerson: '',
                email: '',
                phone: ''
              },
              complianceType: compliance.complianceType || '',
              complianceCategory: compliance.complianceCategory || '',
              regulatoryFramework: compliance.regulatoryFramework || '',
              applicableStandards: compliance.applicableStandards || [],
              complianceLevel: compliance.complianceLevel || '',
              riskLevel: compliance.riskLevel || 'Medium',
              requirements: compliance.requirements || [],
              standards: compliance.standards || [],
              regulations: compliance.regulations || [],
              policies: compliance.policies || [],
              procedures: compliance.procedures || [],
              documents: compliance.documents || [],
              certificates: compliance.certificates || [],
              licenses: compliance.licenses || [],
              permits: compliance.permits || [],
              approvals: compliance.approvals || [],
              assessment: compliance.assessment || {
                overallScore: 0,
                complianceScore: 0,
                riskScore: 0,
                qualityScore: 0,
                safetyScore: 0,
                environmentalScore: 0,
                socialScore: 0,
                governanceScore: 0
              },
              violations: compliance.violations || [],
              nonCompliances: compliance.nonCompliances || [],
              issues: compliance.issues || [],
              correctiveActions: compliance.correctiveActions || [],
              preventiveActions: compliance.preventiveActions || [],
              audit: {
                lastAuditDate: compliance.audit?.lastAuditDate ? new Date(compliance.audit.lastAuditDate).toISOString().split('T')[0] : '',
                nextAuditDate: compliance.audit?.nextAuditDate ? new Date(compliance.audit.nextAuditDate).toISOString().split('T')[0] : '',
                auditor: compliance.audit?.auditor || '',
                auditType: compliance.audit?.auditType || '',
                auditScope: compliance.audit?.auditScope || '',
                auditFindings: compliance.audit?.auditFindings || [],
                auditRecommendations: compliance.audit?.auditRecommendations || []
              },
              certifications: compliance.certifications || {
                iso9001: { status: 'not-applicable', expiryDate: '', certificateNumber: '' },
                iso14001: { status: 'not-applicable', expiryDate: '', certificateNumber: '' },
                ohsas18001: { status: 'not-applicable', expiryDate: '', certificateNumber: '' },
                iso45001: { status: 'not-applicable', expiryDate: '', certificateNumber: '' },
                custom: []
              },
              training: compliance.training || {
                requiredTraining: [],
                completedTraining: [],
                trainingRecords: [],
                competencyAssessments: [],
                skillGaps: []
              },
              monitoring: compliance.monitoring || {
                frequency: '',
                method: '',
                responsible: '',
                reportingSchedule: '',
                escalationProcedure: ''
              },
              riskAssessment: compliance.riskAssessment || {
                identifiedRisks: [],
                riskLevels: [],
                mitigationStrategies: [],
                contingencyPlans: [],
                riskOwners: []
              },
              metrics: compliance.metrics || {
                complianceRate: 0,
                violationCount: 0,
                auditScore: 0,
                trainingCompletion: 0,
                certificationStatus: 0
              },
              review: {
                reviewer: compliance.review?.reviewer || '',
                reviewDate: compliance.review?.reviewDate ? new Date(compliance.review.reviewDate).toISOString().split('T')[0] : '',
                approvalStatus: compliance.review?.approvalStatus || 'pending',
                approvalNotes: compliance.review?.approvalNotes || '',
                nextReviewDate: compliance.review?.nextReviewDate ? new Date(compliance.review.nextReviewDate).toISOString().split('T')[0] : ''
              },
              settings: compliance.settings || {
                autoReminders: true,
                escalationEnabled: true,
                publicVisibility: false,
                requireApproval: true,
                notificationSettings: {
                  email: true,
                  sms: false,
                  portal: true
                }
              }
            });
          } else {
            showError('Failed to load compliance record details.');
            navigate('/subcontracting/compliance');
          }
        } catch (err) {
          console.error('Error loading compliance record:', err);
          showError('Failed to load compliance record details.');
          navigate('/subcontracting/compliance');
        } finally {
          setLoading(false);
        }
      }
    };
    
    loadComplianceData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [complianceId]);

  // Tab configuration
  const tabs = [
    {
      id: 'basic',
      label: 'Compliance Details',
      icon: <Shield size={18} />
    },
    {
      id: 'requirements',
      label: 'Requirements & Standards',
      icon: <FileText size={18} />
    },
    {
      id: 'assessment',
      label: 'Assessment & Audit',
      icon: <Clipboard size={18} />
    },
    {
      id: 'management',
      label: 'Management & Review',
      icon: <Settings size={18} />
    }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleNestedInputChange = (path, key, value) => {
  if (!path.includes('.')) {
    setFormData(prev => ({
      ...prev,
      [path]: {
        ...prev[path],
        [key]: value
      }
    }));
    return;
  }

  const [parent, child] = path.split('.');

  setFormData(prev => ({
    ...prev,
    [parent]: {
      ...prev[parent],
      [child]: {
        ...prev[parent][child],
        [key]: value
      }
    }
  }));
}

  const handleArrayAdd = (path, value) => {
  if (!value || !value.trim()) return;

  if (path.includes('.')) {
    const [parent, child] = path.split('.');
    setFormData(prev => ({
      ...prev,
      [parent]: {
        ...prev[parent],
        [child]: [...(prev[parent]?.[child] || []), value.trim()]
      }
    }));
  } else {
    setFormData(prev => ({
      ...prev,
      [path]: [...(prev[path] || []), value.trim()]
    }));
  }
};


  const handleArrayRemove = (path, index) => {
  if (path.includes('.')) {
    const [parent, child] = path.split('.');
    setFormData(prev => ({
      ...prev,
      [parent]: {
        ...prev[parent],
        [child]: prev[parent][child].filter((_, i) => i !== index)
      }
    }));
  } else {
    setFormData(prev => ({
      ...prev,
      [path]: prev[path].filter((_, i) => i !== index)
    }));
  }
};


  const handleScoreChange = (field, value) => {
    const score = Math.max(0, Math.min(100, parseInt(value) || 0));
    
    setFormData(prev => {
      const updatedData = {
        ...prev,
        assessment: {
          ...prev.assessment,
          [field]: score
        }
      };
      
      // Auto-calculate overall score
      const scores = [
        updatedData.assessment.complianceScore,
        updatedData.assessment.riskScore,
        updatedData.assessment.qualityScore,
        updatedData.assessment.safetyScore,
        updatedData.assessment.environmentalScore,
        updatedData.assessment.socialScore,
        updatedData.assessment.governanceScore
      ];
      
      const overall = Math.round(scores.reduce((sum, score) => sum + score, 0) / scores.length);
      updatedData.assessment.overallScore = overall;
      
      return updatedData;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Don't submit in view mode
    if (viewMode) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Validate required fields
      if (!formData.recordId || !formData.complianceType || !formData.subcontractor.id) {
        showError('Please fill in all required fields (Record ID, Compliance Type, and Subcontractor).');
        setIsSubmitting(false);
        return;
      }

      // Format data for backend - convert date strings to ISO strings, ensure numbers are numeric
      const formatDate = (dateStr) => {
        if (!dateStr) return undefined;
        if (!formData.contractId) {
    showError('Please select an associated contract.');
    return;
  }
        try {
          const date = new Date(dateStr);
          return isNaN(date.getTime()) ? undefined : date.toISOString();
        } catch {
          return undefined;
        }
      };

      const formatNumeric = (value) => {
        if (value === null || value === undefined || value === '') return undefined;
        const num = Number(value);
        return isNaN(num) ? undefined : num;
      };

      const complianceData = {
        contractId: formData.contractId,
        recordId: formData.recordId || undefined,
        recordType: formData.recordType || 'compliance',
        status: formData.status || 'draft',
        priority: formData.priority || 'medium',
        effectiveDate: formatDate(formData.effectiveDate),
        expiryDate: formatDate(formData.expiryDate),
        reviewDate: formatDate(formData.reviewDate),
        subcontractor: formData.subcontractor.id ? {
          id: formData.subcontractor.id,
          name: formData.subcontractor.name || undefined,
          company: formData.subcontractor.company || undefined,
          category: formData.subcontractor.category || undefined,
          contactPerson: formData.subcontractor.contactPerson || undefined,
          email: formData.subcontractor.email || undefined,
          phone: formData.subcontractor.phone || undefined
        } : undefined,
        complianceType: formData.complianceType || undefined,
        complianceCategory: formData.complianceCategory || undefined,
        regulatoryFramework: formData.regulatoryFramework || undefined,
        applicableStandards: Array.isArray(formData.applicableStandards) ? formData.applicableStandards.filter(s => s) : undefined,
        complianceLevel: formData.complianceLevel || undefined,
        riskLevel: formData.riskLevel || undefined,
        requirements: Array.isArray(formData.requirements) ? formData.requirements.filter(r => r) : undefined,
        standards: Array.isArray(formData.standards) ? formData.standards.filter(s => s) : undefined,
        regulations: Array.isArray(formData.regulations) ? formData.regulations.filter(r => r) : undefined,
        policies: Array.isArray(formData.policies) ? formData.policies.filter(p => p) : undefined,
        procedures: Array.isArray(formData.procedures) ? formData.procedures.filter(p => p) : undefined,
        assessment: {
          overallScore: formatNumeric(formData.assessment?.overallScore),
          complianceScore: formatNumeric(formData.assessment?.complianceScore),
          riskScore: formatNumeric(formData.assessment?.riskScore),
          qualityScore: formatNumeric(formData.assessment?.qualityScore),
          safetyScore: formatNumeric(formData.assessment?.safetyScore),
          environmentalScore: formatNumeric(formData.assessment?.environmentalScore),
          socialScore: formatNumeric(formData.assessment?.socialScore),
          governanceScore: formatNumeric(formData.assessment?.governanceScore)
        },
        violations: Array.isArray(formData.violations) ? formData.violations.filter(v => v) : undefined,
        nonCompliances: Array.isArray(formData.nonCompliances) ? formData.nonCompliances.filter(n => n) : undefined,
        issues: Array.isArray(formData.issues) ? formData.issues.filter(i => i) : undefined,
        correctiveActions: Array.isArray(formData.correctiveActions) ? formData.correctiveActions.filter(c => c) : undefined,
        preventiveActions: Array.isArray(formData.preventiveActions) ? formData.preventiveActions.filter(p => p) : undefined,
        audit: {
          lastAuditDate: formatDate(formData.audit?.lastAuditDate),
          nextAuditDate: formatDate(formData.audit?.nextAuditDate),
          auditor: formData.audit?.auditor || undefined,
          auditType: formData.audit?.auditType || undefined,
          auditScope: formData.audit?.auditScope || undefined,
          auditFindings: Array.isArray(formData.audit?.auditFindings) ? formData.audit.auditFindings.filter(f => f) : undefined,
          auditRecommendations: Array.isArray(formData.audit?.auditRecommendations) ? formData.audit.auditRecommendations.filter(r => r) : undefined
        },
        certifications: {
          iso9001: {
            status: formData.certifications?.iso9001?.status || 'not-applicable',
            expiryDate: formatDate(formData.certifications?.iso9001?.expiryDate),
            certificateNumber: formData.certifications?.iso9001?.certificateNumber || undefined
          },
          iso14001: {
            status: formData.certifications?.iso14001?.status || 'not-applicable',
            expiryDate: formatDate(formData.certifications?.iso14001?.expiryDate),
            certificateNumber: formData.certifications?.iso14001?.certificateNumber || undefined
          },
          ohsas18001: {
            status: formData.certifications?.ohsas18001?.status || 'not-applicable',
            expiryDate: formatDate(formData.certifications?.ohsas18001?.expiryDate),
            certificateNumber: formData.certifications?.ohsas18001?.certificateNumber || undefined
          },
          iso45001: {
            status: formData.certifications?.iso45001?.status || 'not-applicable',
            expiryDate: formatDate(formData.certifications?.iso45001?.expiryDate),
            certificateNumber: formData.certifications?.iso45001?.certificateNumber || undefined
          },
          custom: Array.isArray(formData.certifications?.custom) ? formData.certifications.custom.filter(c => c && c.name) : undefined
        },
        monitoring: formData.monitoring ? {
          frequency: formData.monitoring.frequency || undefined,
          method: formData.monitoring.method || undefined,
          responsible: formData.monitoring.responsible || undefined,
          reportingSchedule: formData.monitoring.reportingSchedule || undefined,
          escalationProcedure: formData.monitoring.escalationProcedure || undefined
        } : undefined,
        review: {
          reviewer: formData.review?.reviewer || undefined,
          reviewDate: formatDate(formData.review?.reviewDate),
          approvalStatus: formData.review?.approvalStatus || 'pending',
          approvalNotes: formData.review?.approvalNotes || undefined,
          nextReviewDate: formatDate(formData.review?.nextReviewDate)
        },
        settings: formData.settings ? {
          autoReminders: formData.settings.autoReminders !== undefined ? formData.settings.autoReminders : true,
          escalationEnabled: formData.settings.escalationEnabled !== undefined ? formData.settings.escalationEnabled : true,
          publicVisibility: formData.settings.publicVisibility !== undefined ? formData.settings.publicVisibility : false,
          requireApproval: formData.settings.requireApproval !== undefined ? formData.settings.requireApproval : true,
          notificationSettings: formData.settings.notificationSettings ? {
            email: formData.settings.notificationSettings.email !== undefined ? formData.settings.notificationSettings.email : true,
            sms: formData.settings.notificationSettings.sms !== undefined ? formData.settings.notificationSettings.sms : false,
            portal: formData.settings.notificationSettings.portal !== undefined ? formData.settings.notificationSettings.portal : true
          } : undefined
        } : undefined
      };

      // Remove undefined values to clean up the payload
      const cleanData = JSON.parse(JSON.stringify(complianceData));

      let response;
      if (editMode && complianceId) {
        response = await ComplianceService.updateCompliance(complianceId, cleanData);
        if (response && response.success) {
          success('Compliance record updated successfully!');
        } else {
          showError(response?.message || 'Failed to update compliance record. Please try again.');
        }
      } else {
        response = await ComplianceService.createCompliance(cleanData);
        if (response && response.success) {
          success('Compliance record created successfully!');
        } else {
          showError(response?.message || 'Failed to create compliance record. Please try again.');
        }
      }
      
      if (response && response.success) {
        setTimeout(() => {
          navigate('/subcontracting/compliance');
        }, 1000);
      }
    } catch (err) {
      console.error(`Error ${editMode ? 'updating' : 'creating'} compliance record:`, err);
      const errorMessage = err?.message || err?.response?.data?.message || `Failed to ${editMode ? 'update' : 'create'} compliance record. Please try again.`;
      showError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'basic':
        return renderComplianceDetails();
      case 'requirements':
        return renderRequirementsStandards();
      case 'assessment':
        return renderAssessmentAudit();
      case 'management':
        return renderManagementReview();
      default:
        return renderComplianceDetails();
    }
  };

  const renderComplianceDetails = () => (
    <div className="project-form-section">
      <div className="section-header">
        <h3 className="section-title">Compliance Record Details</h3>
        <p className="section-description">Basic compliance information and record details</p>
      </div>
      
      {/* Add this inside the subcontractor information section */}
<div className="form-group">
  <label className="form-label">Associated Contract *</label>
  <select
    className="form-select"
    value={formData.contractId} // Ensure this matches your state field
    onChange={(e) => handleInputChange('contractId', e.target.value)}
    required
    disabled={loadingContracts}
  >
    <option value="">
      {loadingContracts ? 'Loading contracts...' : 'Select associated contract'}
    </option>
    {contracts.map((contract) => (
      <option key={contract._id} value={contract._id}>
        {contract.displayLabel}
      </option>
    ))}
  </select>
</div>
      <div className="form-grid-4">
        <div className="form-group">
          <label className="form-label">Record ID *</label>
          <input
            type="text"
            className="form-control"
            value={formData.recordId}
            onChange={(e) => handleInputChange('recordId', e.target.value)}
            placeholder="Enter record ID"
            required
            {...getInputProps()}
          />
        </div>
        
        <div className="form-group">
          <label className="form-label">Compliance Type *</label>
          <select
            className="form-select"
            value={formData.complianceType}
            onChange={(e) => handleInputChange('complianceType', e.target.value)}
            required
            {...getInputProps()}
          >
            <option value="">Select compliance type</option>
            <option value="safety">Safety Compliance</option>
            <option value="environmental">Environmental Compliance</option>
            <option value="quality">Quality Compliance</option>
            <option value="financial">Financial Compliance</option>
            <option value="regulatory">Regulatory Compliance</option>
            <option value="contractual">Contractual Compliance</option>
          </select>
        </div>
        
        <div className="form-group">
          <label className="form-label">Compliance Category</label>
          <select
            className="form-select"
            value={formData.complianceCategory}
            onChange={(e) => handleInputChange('complianceCategory', e.target.value)}
            {...getInputProps()}
          >
            <option value="">Select category</option>
            <option value="mandatory">Mandatory</option>
            <option value="voluntary">Voluntary</option>
            <option value="industry">Industry Standard</option>
            <option value="internal">Internal Policy</option>
            <option value="client">Client Requirement</option>
          </select>
        </div>
        
        <div className="form-group">
          <label className="form-label">Status</label>
          <select
            className="form-select"
            value={formData.status}
            onChange={(e) => handleInputChange('status', e.target.value)}
            {...getInputProps()}
          >
            <option value="draft">Draft</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="expired">Expired</option>
            <option value="suspended">Suspended</option>
          </select>
        </div>
        
        <div className="form-group">
          <label className="form-label">Priority</label>
          <select
            className="form-select"
            value={formData.priority}
            onChange={(e) => handleInputChange('priority', e.target.value)}
            {...getInputProps()}
          >
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="critical">Critical</option>
          </select>
        </div>
        
        <div className="form-group">
          <label className="form-label">Effective Date</label>
          <input
            type="date"
            className="form-control"
            value={formData.effectiveDate}
            onChange={(e) => handleInputChange('effectiveDate', e.target.value)}
            {...getInputProps()}
          />
        </div>
        
        <div className="form-group">
          <label className="form-label">Expiry Date</label>
          <input
            type="date"
            className="form-control"
            value={formData.expiryDate}
            onChange={(e) => handleInputChange('expiryDate', e.target.value)}
            {...getInputProps()}
          />
        </div>
        
        <div className="form-group">
          <label className="form-label">Review Date</label>
          <input
            type="date"
            className="form-control"
            value={formData.reviewDate}
            onChange={(e) => handleInputChange('reviewDate', e.target.value)}
          />
        </div>
      </div>
      
      <div className="form-group">
        <h4 className="section-subtitle">Subcontractor Information</h4>
        <div className="form-grid-4">
          <div className="form-group">
            <label className="form-label">Subcontractor *</label>
            <select
              className="form-select"
              value={formData.subcontractor.id}
              onChange={(e) => {
                const selectedId = e.target.value;
                const selectedSubcontractor = subcontractors.find(sub => sub._id === selectedId);
                if (selectedSubcontractor) {
                  setFormData(prev => ({
                    ...prev,
                    subcontractor: {
                      id: selectedSubcontractor._id,
                      name: selectedSubcontractor.name || '',
                      company: selectedSubcontractor.company || '',
                      category: selectedSubcontractor.businessType || selectedSubcontractor.industry || '',
                      contactPerson: selectedSubcontractor.contactPerson || '',
                      email: selectedSubcontractor.email || '',
                      phone: selectedSubcontractor.phone || ''
                    }
                  }));
                } else {
                  handleNestedInputChange('subcontractor', 'id', selectedId);
                }
              }}
              required
              {...getInputProps()}
            >
              <option value="">Select subcontractor</option>
              {subcontractors.map((sub) => (
                <option key={sub._id} value={sub._id}>
                  {sub.name || sub.subcontractorId || 'Unnamed Subcontractor'}
                </option>
              ))}
            </select>
          </div>
          
          <div className="form-group">
            <label className="form-label">Contact Person</label>
            <input
              type="text"
              className="form-control"
              value={formData.subcontractor.contactPerson}
              onChange={(e) => handleNestedInputChange('subcontractor', 'contactPerson', e.target.value)}
              placeholder="Enter contact person name"
              {...getInputProps()}
            />
          </div>
          
          <div className="form-group">
            <label className="form-label">Email</label>
            <input
              type="email"
              className="form-control"
              value={formData.subcontractor.email}
              onChange={(e) => handleNestedInputChange('subcontractor', 'email', e.target.value)}
              placeholder="Enter email address"
              {...getInputProps()}
            />
          </div>
          
          <div className="form-group">
            <label className="form-label">Phone</label>
            <input
              type="tel"
              className="form-control"
              value={formData.subcontractor.phone}
              onChange={(e) => handleNestedInputChange('subcontractor', 'phone', e.target.value)}
              placeholder="Enter phone number"
              {...getInputProps()}
            />
          </div>
        </div>
      </div>
      
      <div className="form-group">
        <h4 className="section-subtitle">Regulatory Framework</h4>
        <div className="form-grid-2">
          <div className="form-group">
            <label className="form-label">Regulatory Framework</label>
            <select
              className="form-select"
              value={formData.regulatoryFramework}
              onChange={(e) => handleInputChange('regulatoryFramework', e.target.value)}
            >
              <option value="">Select framework</option>
              <option value="iso">ISO Standards</option>
              <option value="osha">OSHA Regulations</option>
              <option value="epa">EPA Guidelines</option>
              <option value="federal">Federal Regulations</option>
              <option value="state">State Regulations</option>
              <option value="local">Local Ordinances</option>
            </select>
          </div>
          
          <div className="form-group">
            <label className="form-label">Compliance Level</label>
            <select
              className="form-select"
              value={formData.complianceLevel}
              onChange={(e) => handleInputChange('complianceLevel', e.target.value)}
            >
              <option value="">Select level</option>
              <option value="full">Full Compliance</option>
              <option value="partial">Partial Compliance</option>
              <option value="non-compliant">Non-Compliant</option>
              <option value="under-review">Under Review</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );

  const renderRequirementsStandards = () => (
    <div className="project-form-section">
      <div className="section-header">
        <h3 className="section-title">Requirements & Standards</h3>
        <p className="section-description">Compliance requirements, standards, and applicable regulations</p>
      </div>
      
      <div className="form-group">
        <label className="form-label">Applicable Standards</label>
        <div className="array-input-group">
          {formData.applicableStandards.map((standard, index) => (
            <div key={index} className="array-input-item">
              <input
                type="text"
                className="form-control"
                value={standard}
                onChange={(e) => {
                  const newStandards = [...formData.applicableStandards];
                  newStandards[index] = e.target.value;
                  setFormData(prev => ({ ...prev, applicableStandards: newStandards }));
                }}
                placeholder="Enter standard"
              />
              <button
                type="button"
                className="btn btn-outline-danger btn-sm"
                onClick={() => handleArrayRemove('applicableStandards', index)}
              >
                <X size={16} />
              </button>
            </div>
          ))}
          <div className="array-input-add">
            <input
              type="text"
              className="form-control"
              placeholder="Add new standard"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                   e.preventDefault();
                  handleArrayAdd('applicableStandards', e.target.value);
                  e.target.value = '';
                }
              }}
            />
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={(e) => {
                const input = e.target.previousElementSibling;
                if (input && input.value) {
                  handleArrayAdd('applicableStandards', input.value);
                  input.value = '';
                }
              }}
            >
              <Plus size={16} />
            </button>
          </div>
        </div>
      </div>
      
      <div className="form-group">
        <label className="form-label">Compliance Requirements</label>
        <div className="array-input-group">
          {formData.requirements.map((requirement, index) => (
            <div key={index} className="array-input-item">
              <input
                type="text"
                className="form-control"
                value={requirement}
                onChange={(e) => {
                  const newRequirements = [...formData.requirements];
                  newRequirements[index] = e.target.value;
                  setFormData(prev => ({ ...prev, requirements: newRequirements }));
                }}
                placeholder="Enter requirement"
              />
              <button
                type="button"
                className="btn btn-outline-danger btn-sm"
                onClick={() => handleArrayRemove('requirements', index)}
              >
                <X size={16} />
              </button>
            </div>
          ))}
          <div className="array-input-add">
            <input
              type="text"
              className="form-control"
              placeholder="Add new requirement"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                   e.preventDefault();
                  handleArrayAdd('requirements', e.target.value);
                  e.target.value = '';
                }
              }}
            />
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={(e) => {
                const input = e.target.previousElementSibling;
                if (input && input.value) {
                  handleArrayAdd('requirements', input.value);
                  input.value = '';
                }
              }}
            >
              <Plus size={16} />
            </button>
          </div>
        </div>
      </div>
      
      <div className="form-group">
        <label className="form-label">Regulations</label>
        <div className="array-input-group">
          {formData.regulations.map((regulation, index) => (
            <div key={index} className="array-input-item">
              <input
                type="text"
                className="form-control"
                value={regulation}
                onChange={(e) => {
                  const newRegulations = [...formData.regulations];
                  newRegulations[index] = e.target.value;
                  setFormData(prev => ({ ...prev, regulations: newRegulations }));
                }}
                placeholder="Enter regulation"
              />
              <button
                type="button"
                className="btn btn-outline-danger btn-sm"
                onClick={() => handleArrayRemove('regulations', index)}
              >
                <X size={16} />
              </button>
            </div>
          ))}
          <div className="array-input-add">
            <input
              type="text"
              className="form-control"
              placeholder="Add new regulation"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                   e.preventDefault();
                  handleArrayAdd('regulations', e.target.value);
                  e.target.value = '';
                }
              }}
            />
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={(e) => {
                const input = e.target.previousElementSibling;
                if (input && input.value) {
                  handleArrayAdd('regulations', input.value);
                  input.value = '';
                }
              }}
            >
              <Plus size={16} />
            </button>
          </div>
        </div>
      </div>
      
      <div className="form-group">
        <h4 className="section-subtitle">Certifications and Licenses</h4>
        <div className="form-grid-2">
          <div className="form-group">
            <label className="form-label">ISO 9001:2015</label>
            <select
              className="form-select"
              value={formData.certifications.iso9001.status}
              onChange={(e) => handleNestedInputChange('certifications.iso9001', 'status', e.target.value)}
            >
              <option value="not-applicable">Not Applicable</option>
              <option value="certified">Certified</option>
              <option value="pending">Pending</option>
              <option value="expired">Expired</option>
            </select>
          </div>
          
          <div className="form-group">
            <label className="form-label">ISO 14001:2015</label>
            <select
              className="form-select"
              value={formData.certifications.iso14001.status}
              onChange={(e) => handleNestedInputChange('certifications.iso14001', 'status', e.target.value)}
            >
              <option value="not-applicable">Not Applicable</option>
              <option value="certified">Certified</option>
              <option value="pending">Pending</option>
              <option value="expired">Expired</option>
            </select>
          </div>
          
          <div className="form-group">
            <label className="form-label">OHSAS 18001</label>
            <select
              className="form-select"
              value={formData.certifications.ohsas18001.status}
              onChange={(e) => handleNestedInputChange('certifications.ohsas18001', 'status', e.target.value)}
            >
              <option value="not-applicable">Not Applicable</option>
              <option value="certified">Certified</option>
              <option value="pending">Pending</option>
              <option value="expired">Expired</option>
            </select>
          </div>
          
          <div className="form-group">
            <label className="form-label">ISO 45001:2018</label>
            <select
              className="form-select"
              value={formData.certifications.iso45001.status}
              onChange={(e) => handleNestedInputChange('certifications.iso45001', 'status', e.target.value)}
            >
              <option value="not-applicable">Not Applicable</option>
              <option value="certified">Certified</option>
              <option value="pending">Pending</option>
              <option value="expired">Expired</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );

  const renderAssessmentAudit = () => (
    <div className="project-form-section">
      <div className="section-header">
        <h3 className="section-title">Assessment & Audit</h3>
        <p className="section-description">Compliance assessment scores and audit information</p>
      </div>
      
      <div className="form-group">
        <h4 className="section-subtitle">Compliance Assessment Scores</h4>
        <div className="form-grid-4">
          <div className="form-group">
            <label className="form-label">Compliance Score: {formData.assessment.complianceScore}%</label>
            <input
              type="range"
              className="form-range"
              min="0"
              max="100"
              value={formData.assessment.complianceScore}
              onChange={(e) => handleScoreChange('complianceScore', e.target.value)}
            />
          </div>
          
          <div className="form-group">
            <label className="form-label">Risk Score: {formData.assessment.riskScore}%</label>
            <input
              type="range"
              className="form-range"
              min="0"
              max="100"
              value={formData.assessment.riskScore}
              onChange={(e) => handleScoreChange('riskScore', e.target.value)}
            />
          </div>
          
          <div className="form-group">
            <label className="form-label">Quality Score: {formData.assessment.qualityScore}%</label>
            <input
              type="range"
              className="form-range"
              min="0"
              max="100"
              value={formData.assessment.qualityScore}
              onChange={(e) => handleScoreChange('qualityScore', e.target.value)}
            />
          </div>
          
          <div className="form-group">
            <label className="form-label">Safety Score: {formData.assessment.safetyScore}%</label>
            <input
              type="range"
              className="form-range"
              min="0"
              max="100"
              value={formData.assessment.safetyScore}
              onChange={(e) => handleScoreChange('safetyScore', e.target.value)}
            />
          </div>
          
          <div className="form-group">
            <label className="form-label">Environmental Score: {formData.assessment.environmentalScore}%</label>
            <input
              type="range"
              className="form-range"
              min="0"
              max="100"
              value={formData.assessment.environmentalScore}
              onChange={(e) => handleScoreChange('environmentalScore', e.target.value)}
            />
          </div>
          
          <div className="form-group">
            <label className="form-label">Social Score: {formData.assessment.socialScore}%</label>
            <input
              type="range"
              className="form-range"
              min="0"
              max="100"
              value={formData.assessment.socialScore}
              onChange={(e) => handleScoreChange('socialScore', e.target.value)}
            />
          </div>
          
          <div className="form-group">
            <label className="form-label">Governance Score: {formData.assessment.governanceScore}%</label>
            <input
              type="range"
              className="form-range"
              min="0"
              max="100"
              value={formData.assessment.governanceScore}
              onChange={(e) => handleScoreChange('governanceScore', e.target.value)}
            />
          </div>
        </div>
        
        <div className="form-group">
          <div className="score-display">
            <h4>Overall Score: {formData.assessment.overallScore}%</h4>
            <div className="score-bar">
              <div 
                className="score-fill" 
                style={{ width: `${formData.assessment.overallScore}%` }}
              ></div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="form-group">
        <h4 className="section-subtitle">Audit Information</h4>
        <div className="form-grid-4">
          <div className="form-group">
            <label className="form-label">Last Audit Date</label>
            <input
              type="date"
              className="form-control"
              value={formData.audit.lastAuditDate}
              onChange={(e) => handleNestedInputChange('audit', 'lastAuditDate', e.target.value)}
            />
          </div>
          
          <div className="form-group">
            <label className="form-label">Next Audit Date</label>
            <input
              type="date"
              className="form-control"
              value={formData.audit.nextAuditDate}
              onChange={(e) => handleNestedInputChange('audit', 'nextAuditDate', e.target.value)}
            />
          </div>
          
          <div className="form-group">
            <label className="form-label">Auditor</label>
            <select
              className="form-select"
              value={formData.audit.auditor}
              onChange={(e) => handleNestedInputChange('audit', 'auditor', e.target.value)}
            >
              <option value="">Select auditor</option>
              <option value="john-doe">John Doe</option>
              <option value="jane-smith">Jane Smith</option>
              <option value="mike-johnson">Mike Johnson</option>
            </select>
          </div>
          
          <div className="form-group">
            <label className="form-label">Audit Type</label>
            <select
              className="form-select"
              value={formData.audit.auditType}
              onChange={(e) => handleNestedInputChange('audit', 'auditType', e.target.value)}
            >
              <option value="">Select audit type</option>
              <option value="internal">Internal Audit</option>
              <option value="external">External Audit</option>
              <option value="third-party">Third Party Audit</option>
              <option value="regulatory">Regulatory Audit</option>
            </select>
          </div>
        </div>
      </div>
      
      <div className="form-group">
        <label className="form-label">Audit Findings</label>
        <div className="array-input-group">
          {formData.audit.auditFindings.map((finding, index) => (
            <div key={index} className="array-input-item">
              <input
                type="text"
                className="form-control"
                value={finding}
                onChange={(e) => {
                  const newFindings = [...formData.audit.auditFindings];
                  newFindings[index] = e.target.value;
                  setFormData(prev => ({
                    ...prev,
                    audit: { ...prev.audit, auditFindings: newFindings }
                  }));
                }}
                placeholder="Enter audit finding"
              />
              <button
                type="button"
                className="btn btn-outline-danger btn-sm"
                onClick={() => handleArrayRemove('audit.auditFindings', index)}
              >
                <X size={16} />
              </button>
            </div>
          ))}
          <div className="array-input-add">
            <input
              type="text"
              className="form-control"
              placeholder="Add audit finding"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                   e.preventDefault();
                  handleArrayAdd('audit.auditFindings', e.target.value);
                  e.target.value = '';
                }
              }}
            />
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={(e) => {
                const input = e.target.previousElementSibling;
                if (input && input.value) {
                  handleArrayAdd('audit.auditFindings', input.value);
                  input.value = '';
                }
              }}
            >
              <Plus size={16} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderManagementReview = () => (
    <div className="project-form-section">
      <div className="section-header">
        <h3 className="section-title">Management & Review</h3>
        <p className="section-description">Review process, monitoring, and management settings</p>
      </div>
      
      <div className="form-group">
        <h4 className="section-subtitle">Review Process</h4>
        <div className="form-grid-4">
          <div className="form-group">
            <label className="form-label">Reviewer</label>
            <select
              className="form-select"
              value={formData.review.reviewer}
              onChange={(e) => handleNestedInputChange('review', 'reviewer', e.target.value)}
            >
              <option value="">Select reviewer</option>
              <option value="john-doe">John Doe</option>
              <option value="jane-smith">Jane Smith</option>
              <option value="mike-johnson">Mike Johnson</option>
            </select>
          </div>
          
          <div className="form-group">
            <label className="form-label">Review Date</label>
            <input
              type="date"
              className="form-control"
              value={formData.review.reviewDate}
              onChange={(e) => handleNestedInputChange('review', 'reviewDate', e.target.value)}
            />
          </div>
          
          <div className="form-group">
            <label className="form-label">Approval Status</label>
            <select
              className="form-select"
              value={formData.review.approvalStatus}
              onChange={(e) => handleNestedInputChange('review', 'approvalStatus', e.target.value)}
            >
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="under-review">Under Review</option>
            </select>
          </div>
          
          <div className="form-group">
            <label className="form-label">Next Review Date</label>
            <input
              type="date"
              className="form-control"
              value={formData.review.nextReviewDate}
              onChange={(e) => handleNestedInputChange('review', 'nextReviewDate', e.target.value)}
            />
          </div>
        </div>
      </div>
      
      <div className="form-group">
        <label className="form-label">Approval Notes</label>
        <textarea
          className="form-control"
          rows="4"
          value={formData.review.approvalNotes}
          onChange={(e) => handleNestedInputChange('review', 'approvalNotes', e.target.value)}
          placeholder="Enter approval notes and comments"
        />
      </div>
      
      <div className="form-group">
        <h4 className="section-subtitle">Monitoring Settings</h4>
        <div className="form-grid-4">
          <div className="form-group">
            <label className="form-label">Monitoring Frequency</label>
            <select
              className="form-select"
              value={formData.monitoring.frequency}
              onChange={(e) => handleNestedInputChange('monitoring', 'frequency', e.target.value)}
            >
              <option value="">Select frequency</option>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
              <option value="annually">Annually</option>
            </select>
          </div>
          
          <div className="form-group">
            <label className="form-label">Monitoring Method</label>
            <select
              className="form-select"
              value={formData.monitoring.method}
              onChange={(e) => handleNestedInputChange('monitoring', 'method', e.target.value)}
            >
              <option value="">Select method</option>
              <option value="automated">Automated</option>
              <option value="manual">Manual</option>
              <option value="hybrid">Hybrid</option>
            </select>
          </div>
          
          <div className="form-group">
            <label className="form-label">Responsible Person</label>
            <select
              className="form-select"
              value={formData.monitoring.responsible}
              onChange={(e) => handleNestedInputChange('monitoring', 'responsible', e.target.value)}
            >
              <option value="">Select person</option>
              <option value="john-doe">John Doe</option>
              <option value="jane-smith">Jane Smith</option>
              <option value="mike-johnson">Mike Johnson</option>
            </select>
          </div>
          
          <div className="form-group">
            <label className="form-label">Reporting Schedule</label>
            <select
              className="form-select"
              value={formData.monitoring.reportingSchedule}
              onChange={(e) => handleNestedInputChange('monitoring', 'reportingSchedule', e.target.value)}
            >
              <option value="">Select schedule</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
              <option value="annually">Annually</option>
            </select>
          </div>
        </div>
      </div>
      
      <div className="form-group">
        <h4 className="section-subtitle">Advanced Settings</h4>
        <div className="checkbox-group">
          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              checked={formData.settings.autoReminders}
              onChange={(e) => handleNestedInputChange('settings', 'autoReminders', e.target.checked)}
            />
            <label className="form-check-label">Auto Reminders</label>
          </div>
          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              checked={formData.settings.escalationEnabled}
              onChange={(e) => handleNestedInputChange('settings', 'escalationEnabled', e.target.checked)}
            />
            <label className="form-check-label">Escalation Enabled</label>
          </div>
          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              checked={formData.settings.requireApproval}
              onChange={(e) => handleNestedInputChange('settings', 'requireApproval', e.target.checked)}
            />
            <label className="form-check-label">Require Approval</label>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Breadcrumb - Outside page-container to align with sidebar */}
      <PageBreadcrumb />
      {/* Page Header - Outside page-container to align with sidebar */}
      <PageHeader 
        title={viewMode ? "View Compliance Record" : editMode ? "Edit Compliance Record" : "Create Compliance Record"}
        description={viewMode ? "View compliance record details (Read-only)" : editMode ? "Edit compliance record details" : "Comprehensive compliance management and monitoring system"}
      />
      {loading ? (
        <div className="page-container">
          <div className="text-center p-5">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      ) : (
      <div className="page-container" style={{ width: '100%', maxWidth: '100%' }}>
        {/* Main Content */}
        <div className="project-create-container" style={{ width: '100%', maxWidth: '100%' }}>
        <form onSubmit={handleSubmit} style={{ width: '100%' }} className={viewMode ? 'view-mode' : ''}>
          {/* Tab Navigation */}
          <div className="project-tabs">
            <div className="project-tabs-nav">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  className={`project-tab ${activeTab === tab.id ? 'active' : ''}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  <div className="project-tab-icon">
                    {tab.icon}
                  </div>
                  <div className="project-tab-content">
                    <div className="project-tab-label">{tab.label}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="project-form-card">
            {renderTabContent()}
          </div>

          {/* Form Actions */}
          {!viewMode && (
            <div className="project-form-actions">
              <button
                type="button"
                className="btn btn-outline"
                onClick={() => navigate('/subcontracting/compliance')}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <div className="spinner-border spinner-border-sm me-2" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    {editMode ? 'Updating Record...' : 'Creating Record...'}
                  </>
                ) : (
                  <>
                    <Save size={18} />
                    {editMode ? 'Update Compliance Record' : 'Create Compliance Record'}
                  </>
                )}
              </button>
            </div>
          )}
          {viewMode && (
            <div className="project-form-actions" style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
              <button
                type="button"
                className="btn btn-outline"
                onClick={() => navigate('/subcontracting/compliance')}
              >
                Back to List
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => navigate(`/subcontracting/compliance/edit/${complianceId}`)}
              >
                Edit Compliance Record
              </button>
            </div>
          )}
        </form>
        </div>
      </div>
      )}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
      {viewMode && (
        <style>{`
          .view-mode input,
          .view-mode select,
          .view-mode textarea {
            pointer-events: none;
            opacity: 0.7;
            background-color: #f3f4f6 !important;
            cursor: not-allowed;
          }
          .view-mode .array-input-add,
          .view-mode .btn-outline-danger {
            display: none !important;
          }
          .view-mode button[type="submit"] {
            display: none !important;
          }
        `}</style>
      )}
    </>
  );
};

export default CreateComplianceRecord;